import React, { useState, useEffect } from 'react';
import { Filter, ChevronDown, Trash2 } from 'lucide-react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";








import { Range } from 'react-range';


// Custom Range Slider Component using react-range
const CustomRangeSlider = ({ value, min, max, step = 1, onChange }) => {

  return (
    <div className="space-y-3 w-full">
      <div className="flex gap-2">
        <input
          type="number"
          value={value[0]}
          min={min}
          max={max}
          step={step}
          onChange={(e) => onChange([Number(e.target.value), value[1]])}
          className="w-20 px-2 py-1 text-xs border border-gray-300 rounded focus:border-primary focus:ring-1 focus:ring-primary"
        />
        <span className="text-gray-500 text-xs self-center">to</span>
        <input
          type="number"
          value={value[1]}
          min={min}
          max={max}
          step={step}
          onChange={(e) => onChange([value[0], Number(e.target.value)])}
          className="w-20 px-2 py-1 text-xs border border-gray-300 rounded focus:border-primary focus:ring-1 focus:ring-primary"
        />
      </div>
      <div className="px-2">
        <Range
          step={step}
          min={min}
          max={max}
          values={value}
          onChange={onChange}
          renderTrack={({ props, children }) => (
            <div {...props} className="w-full h-1.5 bg-gray-300 rounded-full">
              {children}
            </div>
          )}
          renderThumb={({ props }) => (
            <div {...props} className="w-4 h-4 bg-blue-500 rounded-full shadow-md" />
          )}
        />
      </div>
    </div>
  );
};

// Custom Accordion Component to replace Material-UI Accordion
const CustomAccordion = ({ title, children, defaultExpanded = true }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="border border-gray-200 rounded-lg mb-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 text-left bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-lg"
      >
        <span className="font-medium text-gray-900">{title}</span>
        <ChevronDown 
          className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
        />
      </button>
      {isExpanded && (
        <div className="p-3 border-t border-gray-200">
          {children}
        </div>
      )}
    </div>
  );
};

// Custom Checkbox Component
const CustomCheckbox = ({ checked, onChange, label }) => {
  return (
    <label className="flex items-center space-x-2 cursor-pointer text-sm text-gray-700 hover:text-gray-900">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary focus:ring-2"
      />
      <span>{label}</span>
    </label>
  );
};

export default function ListingSidebar({ all, staffs, setStaffs }) {
    const [priceRange, setPriceRange] = useState([0, 200]);
    const [distanceRange, setDistanceRange] = useState([0, 100]); // new distance range
    const [impressions, setImpressions] = useState([25, 300]);
    const [startDate, setStartDate] = useState(new Date());
    const [serviceType, setServiceType] = useState([]); // renamed from billboardType
    
    const checkBoxItems = Array.from(new Set(all.flatMap(i => i.servicesOffered)));
    const [seeAll, setSeeAll] = useState(false);
    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const handleChange = (service) => {
        const updatedServiceType = serviceType.includes(service)
            ? serviceType.filter(s => s !== service)
            : [...serviceType, service];
        
        setServiceType(updatedServiceType);
        updateStaffList(updatedServiceType);
    };

    const updateStaffList = () => {
        let filtered = all;

        // Filter by service type
        if (serviceType.length) {
            filtered = filtered.filter(staff => staff.servicesOffered.some(service => serviceType.includes(service)));
        }

        // Filter by price range
        filtered = filtered.filter(staff => staff.preferences?.minHourlyRate >= priceRange[0] && staff.preferences?.minHourlyRate <= priceRange[1]);

        // ✅ Filter by distanceMiles
        filtered = filtered.filter(staff => 
            staff.distanceMiles >= distanceRange[0] && staff.distanceMiles <= distanceRange[1]
        );

        setStaffs(filtered);
    };

    useEffect(() => {
        updateStaffList();
    }, [serviceType, priceRange, distanceRange]);

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 h-fit max-w-full overflow-hidden">
            <div className="flex items-center gap-2 mb-4">
                <Filter className="text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
            </div>

            <div className="space-y-4 w-full">
                {/* Hourly Charge Filter */}
                <CustomAccordion title="Hourly Charge" defaultExpanded={true}>
                    <div className="space-y-2">
                        <div className="text-sm text-gray-600 font-medium">
                            ${priceRange[0]} - ${priceRange[1]}
                        </div>
                        <CustomRangeSlider
                            value={priceRange}
                            min={30}
                            max={75}
                            onChange={(newRange) => setPriceRange(newRange)}
                        />
                    </div>
                </CustomAccordion>

                {/* Distance Filter */}
                <CustomAccordion title="Distance (miles)" defaultExpanded={true}>
                    <div className="space-y-2">
                        <div className="text-sm text-gray-600 font-medium">
                            {distanceRange[0]} - {distanceRange[1]} mi
                        </div>
                        <CustomRangeSlider
                            value={distanceRange}
                            min={0}
                            max={100}
                            step={1}
                            onChange={(newRange) => setDistanceRange(newRange)}
                        />
                    </div>
                </CustomAccordion>

                {/* Service Types Filter */}
                {checkBoxItems.length > 0 && (
                    <CustomAccordion title="Service Types" defaultExpanded={true}>
                        <div className="space-y-2">
                            {checkBoxItems.slice(0, seeAll ? checkBoxItems.length : 5).map((service, index) => (
                                <CustomCheckbox
                                    key={index}
                                    checked={serviceType.includes(service)}
                                    onChange={() => handleChange(service)}
                                    label={service}
                                />
                            ))}
                            {checkBoxItems.length > 5 && (
                                <button
                                    onClick={() => setSeeAll(!seeAll)}
                                    className="text-primary text-sm hover:underline mt-2"
                                >
                                    {seeAll ? 'See Less' : 'See All'}
                                </button>
                            )}
                        </div>
                    </CustomAccordion>
                )}

                {/* Clear Filters */}
                {(serviceType.length > 0 || priceRange[0] !== 0 || priceRange[1] !== 200 || distanceRange[0] !== 0 || distanceRange[1] !== 100) && (
                    <button
                        onClick={() => {
                            setServiceType([]);
                            setPriceRange([0, 200]);
                            setDistanceRange([0, 100]);
                        }}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                        <Trash2 className="!text-base" />
                        Clear All Filters
                    </button>
                )}
            </div>
        </div>
    );
}
